// My Designs view — full grid with toolbar, filters, sort, bulk mode.
// Per spec §13.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Layers, Folder as FolderIcon, FolderCog, CheckSquare } from 'lucide-react';
import {
  listDesigns,
  listFolders,
  listAgents,
  patchDesign,
  reorderDesign,
  forkDesign,
  deleteDesign,
  restoreDesign,
  bulkArchive,
  bulkPin,
  bulkReorder,
  assignAgentsToDesign,
  dispatchCartAdd,
  StudioApiError,
} from '../../lib/api';
import type { Design, DesignStatus, Folder, SortOption } from '../../lib/types';
import { qk } from '../../lib/queryClient';
import { useStudio } from '../../lib/store';
import { DesignCard } from '../../components/DesignCard';
import { SkeletonGrid } from '../../components/LoadingSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { FolderPicker } from '../../components/FolderPicker';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AgentPicker } from '../../components/AgentPicker';
import './my-designs.css';

const PRODUCT_FILTERS = [
  { id: 'all', label: 'All types', match: (_d: Design) => true },
  { id: 'mailers', label: 'Mailers', match: (d: Design) => /mailer/i.test(d.shopify.product_title ?? '') || /mailer/i.test(d.shopify.product_type ?? '') },
  { id: 'postcards', label: 'Postcards', match: (d: Design) => /postcard/i.test(d.shopify.product_title ?? '') || /postcard/i.test(d.shopify.product_type ?? '') },
  { id: 'signs', label: 'Signs', match: (d: Design) => /sign|rider/i.test(d.shopify.product_title ?? '') },
  { id: 'gift-tags', label: 'Gift Tags', match: (d: Design) => /gift|tag/i.test(d.shopify.product_title ?? '') },
  { id: 'door-hangers', label: 'Door Hangers', match: (d: Design) => /door|hanger/i.test(d.shopify.product_title ?? '') },
];

const SORT_OPTIONS: Array<{ id: SortOption; label: string }> = [
  { id: 'recently_edited', label: 'Recently edited' },
  { id: 'recently_ordered', label: 'Recently ordered' },
  { id: 'most_ordered', label: 'Most ordered' },
  { id: 'name', label: 'Name (A–Z)' },
];

export function MyDesignsView() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [sort, setSort] = useState<SortOption>('recently_ordered');
  const [showArchived, setShowArchived] = useState(false);
  // Folder filter: null = all designs (default), 'no-folder' = uncategorized,
  // any uuid = that folder. Stored as string | null where the special token
  // 'no-folder' is treated as a sentinel for "designs with folder_id = NULL".
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  // 'fromMD' tab — filters to designs Kimi attached for the customer
  // (source_kind='attached'). Mutually exclusive with showArchived and
  // active folder filters since it's its own logical view.
  const [fromMd, setFromMd] = useState(false);
  const [folderPickerMode, setFolderPickerMode] = useState<'move' | 'manage' | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  // "By team member" filter — null = no agent filter, or a specific agent id.
  const [filterAgentId, setFilterAgentId] = useState<string | null>(null);

  // Debounce search 300ms
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const bulkMode = useStudio((s) => s.bulkMode);
  const selected = useStudio((s) => s.selected);
  const toggleSelect = useStudio((s) => s.toggleSelect);
  const enterBulk = useStudio((s) => s.enterBulk);
  const exitBulk = useStudio((s) => s.exitBulk);
  const clearSelected = useStudio((s) => s.clearSelected);
  const pushToast = useStudio((s) => s.pushToast);
  const openDetail = useStudio((s) => s.openDetail);

  // Keyboard shortcuts in bulk mode: esc exits, cmd/ctrl-A selects all
  // visible designs in the current filtered view.
  useEffect(() => {
    if (!bulkMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        exitBulk();
        return;
      }
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && (e.key === 'a' || e.key === 'A')) {
        // Don't hijack cmd-A inside form inputs.
        const t = e.target as HTMLElement;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
          return;
        }
        e.preventDefault();
        // Selection target = whatever's currently visible (post-filter, post-search).
        // We compute this inline rather than from store state so it tracks the
        // user's current view, not the cached unfiltered list.
        const grid = document.querySelector<HTMLElement>('.my-designs__grid');
        const ids = Array.from(grid?.querySelectorAll<HTMLElement>('[data-design-id]') ?? [])
          .map((el) => el.dataset.designId)
          .filter((s): s is string => !!s);
        ids.forEach((id) => {
          if (!selected.has(id)) toggleSelect(id);
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [bulkMode, exitBulk, selected, toggleSelect]);

  const query = useQuery({
    queryKey: qk.designs({
      debouncedSearch,
      sort,
      showArchived,
      activeFolder,
      fromMd,
      filterAgentId,
      pinnedFirst: true,
    }),
    queryFn: () =>
      listDesigns({
        search: debouncedSearch || undefined,
        sort,
        archived: showArchived,
        folder: activeFolder ?? undefined,
        source_kind: fromMd ? 'attached' : undefined,
        agent_id: filterAgentId ?? undefined,
        // Pinned designs always float to the top of the library, regardless
        // of sort order. Matches Quick Reorder's behavior and gives power
        // users (agents who pin their go-to listings) a quick view of
        // what they touch most.
        pinned_first: true,
        limit: 60,
      }),
  });

  const foldersQ = useQuery({
    queryKey: qk.folders,
    queryFn: listFolders,
  });
  const folders: Folder[] = foldersQ.data?.folders ?? [];
  // Show non-system folders in the filmstrip (system "Archived" folder is
  // surfaced via the archive toggle, not via a chip).
  const visibleFolders = folders.filter((f) => !f.is_system);

  const designs = query.data?.items ?? [];

  // Data-driven filter chips: only render a chip when at least one design
  // in the customer's library actually matches it. No point showing
  // "Postcards" if they've never ordered a postcard. "All types" always
  // renders so the customer has a way to clear an active filter.
  const visibleFilters = PRODUCT_FILTERS.filter(
    (f) => f.id === 'all' || designs.some(f.match),
  );
  // If the active filter is no longer in the visible list (e.g. user had
  // 'postcards' selected and then archived their last postcard), reset.
  useEffect(() => {
    if (filter !== 'all' && !visibleFilters.some((f) => f.id === filter)) {
      setFilter('all');
    }
  }, [filter, visibleFilters]);

  // Apply client-side product type filter (cheap, since we already have rows).
  const activeFilter = PRODUCT_FILTERS.find((f) => f.id === filter);
  const filtered = activeFilter ? designs.filter(activeFilter.match) : designs;

  const reorderMut = useMutation({
    mutationFn: async (id: string) => {
      const desc = await reorderDesign(id);
      const cartUrl = await dispatchCartAdd(desc);
      return cartUrl;
    },
    onSuccess: (cartUrl) => {
      window.location.href = cartUrl;
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err instanceof StudioApiError ? err.message : 'Could not add to cart' });
    },
  });

  const forkMut = useMutation({
    mutationFn: async (id: string) => forkDesign(id),
    onSuccess: (resp) => {
      window.location.href = resp.editor_url;
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err instanceof StudioApiError ? err.message : 'Could not fork design' });
    },
  });

  const pinMut = useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) =>
      patchDesign(id, { is_pinned: pinned }),
    onMutate: async ({ id, pinned }) => {
      await qc.cancelQueries({ queryKey: ['designs'] });
      // Optimistic toggle: scan all 'designs' caches and flip the matching row.
      qc.getQueriesData<{ items: Design[] } | undefined>({ queryKey: ['designs'] }).forEach(([key, val]) => {
        if (!val) return;
        qc.setQueryData(key, {
          ...val,
          items: val.items.map((d) => (d.id === id ? { ...d, is_pinned: pinned } : d)),
        });
      });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['designs'] });
      pushToast({ type: 'error', message: 'Could not update pin' });
    },
  });

  const archiveMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const resp = await bulkArchive(ids);
      return { resp, ids };
    },
    onSuccess: ({ resp, ids }) => {
      // Undo toast — gives the customer 8 seconds to revert before the
      // toast auto-dismisses. Click "Undo" → restore each design id in
      // parallel, refresh the grid, confirm with a second toast.
      pushToast({
        type: 'success',
        message: `Archived ${resp.archived} design${resp.archived === 1 ? '' : 's'}`,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await Promise.all(ids.map((id) => restoreDesign(id).catch(() => null)));
              await qc.invalidateQueries({ queryKey: ['designs'] });
              pushToast({ type: 'success', message: `Restored ${ids.length} design${ids.length === 1 ? '' : 's'}` });
            } catch {
              pushToast({ type: 'error', message: 'Could not undo archive' });
            }
          },
        },
      });
      exitBulk();
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: () => pushToast({ type: 'error', message: 'Could not archive selection' }),
  });

  const restoreMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.all(
        ids.map((id) => restoreDesign(id).then(() => true).catch(() => false)),
      );
      return { restored: results.filter(Boolean).length, total: ids.length };
    },
    onSuccess: ({ restored, total }) => {
      pushToast({
        type: 'success',
        message: `Restored ${restored} of ${total} design${total === 1 ? '' : 's'}`,
      });
      exitBulk();
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: () => pushToast({ type: 'error', message: 'Could not restore selection' }),
  });

  // Bulk assign — for each selected design, attach the chosen agent ids.
  const bulkAssignAgentsMut = useMutation({
    mutationFn: async (agentIds: string[]) => {
      const designIds = Array.from(selected);
      const results = await Promise.allSettled(
        designIds.map((designId) => assignAgentsToDesign(designId, agentIds)),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      return { succeeded, total: designIds.length, agentCount: agentIds.length };
    },
    onSuccess: ({ succeeded, agentCount }) => {
      pushToast({
        type: 'success',
        message: `Assigned ${agentCount} member${agentCount === 1 ? '' : 's'} to ${succeeded} design${succeeded === 1 ? '' : 's'}`,
      });
      setShowAgentPicker(false);
      exitBulk();
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: () => pushToast({ type: 'error', message: 'Could not assign team members' }),
  });

  // Active agent filter — pulls the agent name into the chip label so the
  // user knows what they're filtering by.
  const agentsForFilterQuery = useQuery({
    queryKey: ['agents-for-filter'],
    queryFn: () => listAgents({ archived: false }),
    enabled: filterAgentId !== null,
  });
  const filterAgent =
    filterAgentId && agentsForFilterQuery.data
      ? agentsForFilterQuery.data.agents.find((a) => a.id === filterAgentId)
      : null;

  const bulkPinMut = useMutation({
    mutationFn: async ({ ids, pinned }: { ids: string[]; pinned: boolean }) => bulkPin(ids, pinned),
    onSuccess: (resp, vars) => {
      pushToast({
        type: 'success',
        message: vars.pinned ? `Pinned ${resp.updated}` : `Unpinned ${resp.updated}`,
      });
      exitBulk();
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
  });

  const bulkReorderMut = useMutation({
    mutationFn: async () => {
      const items = Array.from(selected).map((id) => {
        const d = designs.find((x) => x.id === id);
        return {
          design_id: id,
          quantity: d?.rollups.last_ordered_qty ?? 1,
        };
      });
      const desc = await bulkReorder(items);
      return dispatchCartAdd(desc);
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => pushToast({ type: 'error', message: 'Could not reorder selection' }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteDesign,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Design moved to trash' });
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
  });

  const isLoading = query.isLoading || query.isPending;

  return (
    <section aria-label="My Designs">
      <header className="view-head">
        <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 8 }}>
          Your library
        </p>
        <h1>My Designs</h1>
        <p className="sub">
          Every design you&rsquo;ve ordered. Reorder a favorite or edit a copy without starting from scratch.
        </p>
      </header>

      {/* Folder filmstrip — secondary nav row. Reads quieter than the
          main toolbar below. Includes:
            - All (default)
            - Each user-created folder
            - Unsorted (designs with folder_id = NULL)
            - Archived (toggles showArchived; clears any folder filter)
            - Manage folders (opens FolderPicker in manage mode) */}
      <div className="folder-strip" role="tablist" aria-label="Folder filter">
        <button
          type="button"
          role="tab"
          aria-selected={activeFolder === null && !showArchived && !fromMd}
          className={`folder-strip__chip${activeFolder === null && !showArchived && !fromMd ? ' is-active' : ''}`}
          onClick={() => {
            setActiveFolder(null);
            setShowArchived(false);
            setFromMd(false);
          }}
        >
          All
          {query.data?.total !== undefined && (
            <span className="count">{query.data.total}</span>
          )}
        </button>
        {visibleFolders.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={activeFolder === f.id && !showArchived && !fromMd}
            className={`folder-strip__chip${activeFolder === f.id && !showArchived && !fromMd ? ' is-active' : ''}`}
            onClick={() => {
              setShowArchived(false);
              setFromMd(false);
              setActiveFolder((cur) => (cur === f.id ? null : f.id));
            }}
          >
            <FolderIcon size={13} aria-hidden="true" strokeWidth={1.6} />
            {f.name}
            <span className="count">{f.design_count}</span>
          </button>
        ))}
        <button
          type="button"
          role="tab"
          aria-selected={activeFolder === 'none' && !showArchived && !fromMd}
          className={`folder-strip__chip${activeFolder === 'none' && !showArchived && !fromMd ? ' is-active' : ''}`}
          onClick={() => {
            setShowArchived(false);
            setFromMd(false);
            setActiveFolder((cur) => (cur === 'none' ? null : 'none'));
          }}
        >
          Unsorted
        </button>
        <span className="folder-strip__divider" aria-hidden="true" />
        <button
          type="button"
          role="tab"
          aria-selected={fromMd}
          className={`folder-strip__chip${fromMd ? ' is-active' : ''}`}
          onClick={() => {
            setActiveFolder(null);
            setShowArchived(false);
            setFromMd((v) => !v);
          }}
        >
          From Market Dwellings
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={showArchived}
          className={`folder-strip__chip${showArchived ? ' is-active' : ''}`}
          onClick={() => {
            setActiveFolder(null);
            setFromMd(false);
            setShowArchived((v) => !v);
          }}
        >
          Archived
        </button>
        <button
          type="button"
          className="folder-strip__manage"
          onClick={() => setFolderPickerMode('manage')}
        >
          <FolderCog size={14} aria-hidden="true" strokeWidth={1.6} />
          Manage folders
        </button>
      </div>

      {/* Main toolbar — flat flex row matching mockup `.d-toolbar`:
          search input + filter chips + sort dropdown (right-aligned via
          margin-left:auto). Wraps naturally when space runs out. */}
      <div className="my-designs__toolbar">
        <div className="my-designs__search">
          <Search size={18} className="my-designs__search-icon" aria-hidden="true" strokeWidth={1.6} />
          <input
            type="search"
            placeholder="Search by name, address, or product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search designs"
          />
        </div>
        {visibleFilters.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`chip${filter === f.id ? ' is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        {/* Active "By team member" filter chip — visible whenever a member
            is filtered. Click the X to clear. The picker for choosing a
            member is below in the toolbar (see "Filter by team"). */}
        {filterAgent && (
          <button
            type="button"
            className="chip is-active chip--filter-active"
            onClick={() => setFilterAgentId(null)}
            title="Clear team member filter"
          >
            {filterAgent.first_name}
            {filterAgent.last_name ? ` ${filterAgent.last_name}` : ''}
            <span aria-hidden="true" style={{ marginLeft: 4, opacity: 0.7 }}>×</span>
          </button>
        )}
        {!filterAgent && (
          <AgentFilterDropdown
            value={filterAgentId}
            onChange={(id) => setFilterAgentId(id)}
          />
        )}
        <label htmlFor="md-sort" className="visually-hidden">Sort</label>
        <select
          id="md-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortOption)}
          className="chip chip--select my-designs__sort-select"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.id} value={s.id}>
              Sort: {s.label}
            </option>
          ))}
        </select>
        {/* Bulk-select entry. While bulk mode is active, clicking flips to
            "Done" so the user has a visible exit on the toolbar in addition
            to the floating bulk bar's Cancel button + the Esc keyboard
            shortcut. */}
        {bulkMode ? (
          <button
            type="button"
            className="chip chip--bulk is-active"
            onClick={() => exitBulk()}
            aria-pressed="true"
          >
            <CheckSquare size={14} aria-hidden="true" strokeWidth={1.8} />
            Done
          </button>
        ) : (
          <button
            type="button"
            className="chip chip--bulk"
            onClick={() => enterBulk()}
            aria-pressed="false"
            title="Enter selection mode (Esc to exit, ⌘A to select all)"
          >
            <CheckSquare size={14} aria-hidden="true" strokeWidth={1.8} />
            Select
          </button>
        )}
      </div>

      {isLoading ? (
        <SkeletonGrid count={9} variant="design" />
      ) : filtered.length === 0 ? (
        showArchived ? (
          <EmptyState
            icon={Layers}
            title="Your archive is empty — no clutter, well done."
            body="When you archive designs, they tuck themselves away here. You can restore any of them with one click, or permanently delete forks you don't need (Market Dwellings designs always stay safe — they only archive, never delete)."
            ctaLabel="Back to all designs"
            onCta={() => {
              setShowArchived(false);
              setActiveFolder(null);
            }}
          />
        ) : activeFolder ? (
          <EmptyState
            title={activeFolder === 'none' ? 'No unsorted designs.' : 'This folder is empty.'}
            body={
              activeFolder === 'none'
                ? 'Every design is filed in a folder. Click All to see them.'
                : 'Move some designs into this folder, or click All to see everything.'
            }
            ctaLabel="Show all designs"
            onCta={() => setActiveFolder(null)}
          />
        ) : debouncedSearch || filter !== 'all' ? (
          <EmptyState
            title="No designs match that."
            body="Try a different search, or clear your filters."
            ctaLabel="Clear filters"
            onCta={() => {
              setSearch('');
              setFilter('all');
            }}
          />
        ) : (
          <EmptyState
            icon={Layers}
            title="Your designs will live here."
            body="Once you place an order, your customized design saves here — ready to reorder, remix, or share with your team forever."
            ctaLabel="Shop products"
            onCta={() => {
              // No template gallery — MD's customer flow is product page →
              // DH editor → checkout, with the design saving to the library
              // on order. Send first-timers to the storefront entry point.
              window.location.href = `https://${query.data?.items[0]?.shopify.shop_domain ?? 'portal.marketdwellings.com'}/collections/all`;
            }}
          />
        )
      ) : (
        <>
          <div className="my-designs__grid">
            {filtered.map((d) => (
              <DesignCard
                key={d.id}
                design={d}
                bulkMode={bulkMode}
                selected={selected.has(d.id)}
                onClick={openDetail}
                onSelect={toggleSelect}
                onReorder={(id) => reorderMut.mutate(id)}
                onEditCopy={(id) => forkMut.mutate(id)}
                onPinToggle={(id) => pinMut.mutate({ id, pinned: !d.is_pinned })}
              />
            ))}
          </div>

          {bulkMode && (
            <div className="bulk-bar" role="region" aria-label="Bulk actions">
              <span className="bulk-bar__count">
                {selected.size === 0
                  ? `Select designs (${filtered.length} visible)`
                  : `${selected.size} of ${filtered.length} selected`}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => {
                  // Toggle: select all visible if any unselected, else clear.
                  const allVisibleSelected = filtered.every((d) => selected.has(d.id));
                  if (allVisibleSelected) {
                    clearSelected();
                  } else {
                    filtered.forEach((d) => {
                      if (!selected.has(d.id)) toggleSelect(d.id);
                    });
                  }
                }}
              >
                {filtered.every((d) => selected.has(d.id)) && filtered.length > 0
                  ? 'Clear'
                  : 'Select all'}
              </button>
              {selected.size > 0 && (
                <>
                  {/* Reorder is meaningless for archived designs (they're
                      not ordered as-is) so we hide it in archived view. */}
                  {!showArchived && (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => bulkReorderMut.mutate()}
                    >
                      Reorder all
                    </button>
                  )}
                  {!showArchived && (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => bulkPinMut.mutate({ ids: Array.from(selected), pinned: true })}
                    >
                      Pin
                    </button>
                  )}
                  {!showArchived && (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => setFolderPickerMode('move')}
                    >
                      Move to folder
                    </button>
                  )}
                  {/* Assign to team member — opens picker modal. Allowed in
                      archived view too, since you might want to retroactively
                      tag old designs. */}
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setShowAgentPicker(true)}
                  >
                    Assign to team member
                  </button>
                  {showArchived ? (
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => restoreMut.mutate(Array.from(selected))}
                      disabled={restoreMut.isPending}
                    >
                      {restoreMut.isPending ? 'Restoring…' : 'Restore'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => archiveMut.mutate(Array.from(selected))}
                      disabled={archiveMut.isPending}
                    >
                      {archiveMut.isPending ? 'Archiving…' : 'Archive'}
                    </button>
                  )}
                  {/* Ship 6: Delete only in Archived view. Designs in the
                      active library can be archived but never hard-deleted —
                      the archive step is a forced reconsideration. The
                      server enforces the same rule (master protection
                      applies even within archived view), so a fork-only
                      bulk request always goes through cleanly. Customer-
                      friendly default: archive first, delete later. */}
                  {showArchived && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--danger"
                      onClick={() => setConfirmBulkDelete(true)}
                    >
                      Delete permanently
                    </button>
                  )}
                </>
              )}
              <button type="button" className="btn btn--ghost" onClick={exitBulk}>
                Done
              </button>
            </div>
          )}
        </>
      )}

      <FolderPicker
        open={folderPickerMode !== null}
        mode={folderPickerMode ?? 'move'}
        designIds={folderPickerMode === 'move' ? Array.from(selected) : []}
        onClose={() => setFolderPickerMode(null)}
        onMoved={() => exitBulk()}
      />

      <AgentPicker
        open={showAgentPicker}
        title={`Assign team member${selected.size === 1 ? '' : 's'} to ${selected.size} design${selected.size === 1 ? '' : 's'}`}
        subtitle="Pick one or more team members. Existing assignments are preserved."
        confirmLabel={`Assign ${selected.size === 1 ? '1 design' : `${selected.size} designs`}`}
        onClose={() => setShowAgentPicker(false)}
        onConfirm={(agentIds) => bulkAssignAgentsMut.mutate(agentIds)}
        busy={bulkAssignAgentsMut.isPending}
      />

      {/* Ship 6: bulk delete confirm. Now lives in archived view only. We
          partition the selection into forks (deletable) and protected
          masters (archive-only) so the dialog can give an honest count
          before the server returns 409s. Forks get the destructive flow;
          masters are filtered out client-side so the customer doesn't see
          mysterious failures. */}
      {(() => {
        const ids = Array.from(selected);
        const itemsById = new Map(filtered.map((d) => [d.id, d]));
        const forkIds = ids.filter((id) => itemsById.get(id)?.source_kind === 'forked');
        const protectedCount = ids.length - forkIds.length;
        return (
          <ConfirmDialog
            open={confirmBulkDelete}
            title={`Permanently delete ${forkIds.length} version${forkIds.length === 1 ? '' : 's'}?`}
            body={
              <>
                <p>
                  This can&rsquo;t be undone. The personalized version{forkIds.length === 1 ? '' : 's'} will
                  be removed from your library entirely.
                </p>
                {protectedCount > 0 && (
                  <p style={{ marginTop: 8, fontSize: '0.88rem', color: 'var(--ink-soft)' }}>
                    {protectedCount} master design{protectedCount === 1 ? '' : 's'}{' '}
                    {protectedCount === 1 ? 'is' : 'are'} protected from deletion and will stay in your archive.
                  </p>
                )}
              </>
            }
            confirmLabel={`Delete ${forkIds.length}`}
            destructive
            busy={deleteMut.isPending}
            onCancel={() => setConfirmBulkDelete(false)}
            onConfirm={() => {
              forkIds.forEach((id) => deleteMut.mutate(id));
              setConfirmBulkDelete(false);
              exitBulk();
            }}
          />
        );
      })()}
    </section>
  );
}

// Suppress unused warnings for status type while we wire detail panel later.
export type { DesignStatus };

/** Dropdown chip for filtering My Designs by an assigned team member.
 *  Loads the customer's roster lazily (only fetches once focused). */
function AgentFilterDropdown({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const agentsQuery = useQuery({
    queryKey: ['agents', { archived: false }],
    queryFn: () => listAgents({ archived: false }),
    enabled: open,
  });
  return (
    <div className="agent-filter">
      <button
        type="button"
        className={`chip${value ? ' is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value ? 'Team member' : 'By team member'} ▾
      </button>
      {open && (
        <div className="agent-filter__menu" role="listbox" onMouseLeave={() => setOpen(false)}>
          <button
            type="button"
            className="agent-filter__item"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            Anyone
          </button>
          {agentsQuery.isLoading ? (
            <div className="agent-filter__loading">Loading…</div>
          ) : (
            (agentsQuery.data?.agents ?? []).map((a) => (
              <button
                type="button"
                key={a.id}
                className={`agent-filter__item${value === a.id ? ' is-active' : ''}`}
                onClick={() => {
                  onChange(a.id);
                  setOpen(false);
                }}
              >
                {a.headshot_url ? (
                  <img src={a.headshot_url} alt="" className="agent-filter__avatar" />
                ) : (
                  <span className="agent-filter__avatar agent-filter__avatar--placeholder">
                    {a.first_name.charAt(0)}
                  </span>
                )}
                <span>
                  {a.first_name}
                  {a.last_name ? ` ${a.last_name}` : ''}
                  {a.is_self && ' (you)'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
